import { useContext } from "react";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { Link, useLocation } from "react-router-dom";
import { AppRolesContext } from "../../contexts/AppRolesContext";
import { RoleName } from "../models/roleNames";

import { SubNav } from "../ui/SubNav";

import './NavigationBar.css';

/**
 * Renders the navbar component with a sign-in or sign-out button depending on whether or not a user is authenticated
 * @param props 
 */
export const NavigationBar = () => {
  const appRolesCtx = useContext(AppRolesContext);
  const { pathname } = useLocation();
  const path = pathname.toLowerCase();

  const isHome = path.includes('home') || path === '';
  const homeClassNames = `navigation-bar__li${isHome ? ' navigation-bar__li--current' : ''}`;

  const isProfile = path.includes('profile');
  const profileClassNames = `navigation-bar__li${isProfile ? ' navigation-bar__li--current' : ''}`;

  const isSharedServices = path.includes('shared-services');
  const sharedServicesClassNames = `navigation-bar__li${isSharedServices ? ' navigation-bar__li--current' : ''}`;

  const isSupport = path.includes('support');
  const supportClassNames = `navigation-bar__li${isSupport ? ' navigation-bar__li--current' : ''}`;

  return (
    <>
      <div className="region region-pre-content">
        <div id="block-mainnavigation" className="block block-superfish block-superfishmain navigation-bar">
          <AuthenticatedTemplate>
            <div id="superfish-main" className="navigation-bar__wrapper sf-menu sf-main sf-horizontal sf-style-default cprd-menu sf-js-enabled">
              <nav className="govuk-width-container layout-container navigation-bar__layout-container">
                <ul className="navigation-bar__navbar">
                  <li className={homeClassNames}>
                    <Link to='/home' className="btn font-weight-bold navigation-bar__item"><WorkspacesIcon /> Workpaces</Link>
                  </li>
                  <li className={profileClassNames}>
                    <Link to='/profile' className="btn font-weight-bold navigation-bar__item"><ProfileIcon /> Profile</Link>
                  </li>
                  <li className={supportClassNames}>
                    <Link to="/support" className="btn font-weight-bold navigation-bar__item"><SupportIcon /> Support</Link>
                  </li>
                  {appRolesCtx.roles?.includes(RoleName.TREAdmin) && (
                    <li className={sharedServicesClassNames}>
                      <Link to="/shared-services" className="btn font-weight-bold navigation-bar__item"><SharedServicesIcon /> Shared services</Link>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          </AuthenticatedTemplate>
          {(appRolesCtx.roles?.includes(RoleName.TREAdmin)) && (
            <AuthenticatedTemplate>
              <SubNav />
            </AuthenticatedTemplate>
          )}
        </div>
      </div>
    </>
  );
};

const WorkspacesIcon = () => {
  return (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_2158_1304)">
        <path className="navigation-bar__icon" fillRule="evenodd" clipRule="evenodd" d="M6.22197 12.9447C6.22197 13.3567 6.38604 13.7526 6.6777 14.0443C6.96936 14.3359 7.36531 14.5 7.77801 14.5H12.4447C12.8566 14.5 13.2526 14.3359 13.5443 14.0443C13.8359 13.7526 14 13.3567 14 12.9447V9.83333C14 9.42136 13.8359 9.02542 13.5443 8.73375C13.2526 8.44209 12.8566 8.27803 12.4447 8.27803H7.77801C7.36531 8.27803 6.96937 8.44209 6.6777 8.73375C6.38603 9.02542 6.22197 9.42136 6.22197 9.83333V12.9447ZM3.11136 8.27803H1.55533C1.14335 8.27803 0.747413 8.44209 0.455747 8.73375C0.16408 9.02542 1.81198e-05 9.42136 1.81198e-05 9.83333V12.9447C1.81198e-05 13.3567 0.164082 13.7526 0.455747 14.0443C0.747411 14.3359 1.14335 14.5 1.55533 14.5H3.11136C3.52333 14.5 3.91927 14.3359 4.21094 14.0443C4.50261 13.7526 4.66667 13.3567 4.66667 12.9447V9.83333C4.66667 9.42136 4.5026 9.02542 4.21094 8.73375C3.91928 8.44209 3.52333 8.27803 3.11136 8.27803ZM14 2.05531C14 1.64333 13.8359 1.24739 13.5443 0.955729C13.2526 0.664061 12.8567 0.5 12.4447 0.5H1.55531C1.14333 0.5 0.747395 0.664062 0.455729 0.955729C0.164061 1.24739 0 1.64333 0 2.05531V5.16667C0 5.57864 0.164062 5.97458 0.455729 6.26625C0.747393 6.55791 1.14333 6.72197 1.55531 6.72197H12.4447C13.3037 6.72197 14 6.02634 14 5.16667V2.05531Z" fill="#0B0C0C" />
      </g>
      <defs>
        <clipPath id="clip0_2158_1304">
          <rect width="14" height="14" fill="white" transform="translate(0 0.5)"/>
        </clipPath>
      </defs>
    </svg>
  )
}

const ProfileIcon = () => {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path className="navigation-bar__icon" fillRule="evenodd" clipRule="evenodd" d="M7.62252 8.74185C5.20314 8.74185 3.24568 6.78439 3.24568 4.365C3.24568 1.95746 5.20314 0 7.62252 0C10.0301 0 11.9875 1.95746 11.9875 4.365C11.9875 6.78439 10.0301 8.74185 7.62252 8.74185ZM7.62252 16H14.8082C15.0394 16 15.2338 15.8056 15.2338 15.5625V13.605C15.2338 13.52 15.1851 13.3863 15.1488 13.3375C13.5194 11.1487 10.2613 10.0549 7.62315 10.0549C4.97257 10.0549 1.7143 11.1493 0.0850005 13.3375C0.0487509 13.3863 0 13.52 0 13.605V15.5625C0 15.8056 0.19437 16 0.437508 16H7.62252Z" fill="#0B0C0C"/>
    </svg>
  )
}

const SupportIcon = () => {
  return (
    <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path className="navigation-bar__icon" d="M22.0604 0H9.33995C8.84851 0 8.45117 0.39734 8.45117 0.888778V4.42022H14.321C15.5368 4.42022 16.4959 5.40216 16.4959 6.59512V9.35466L19.208 12.0429C19.6994 12.5343 20.5407 12.1835 20.5407 11.482V9.14365H22.0606C22.5521 9.14365 22.9494 8.7463 22.9494 8.25487V0.865067C22.9494 0.397388 22.5518 0 22.0604 0Z" fill="#0B0C0C"/>
      <path className="navigation-bar__icon" d="M14.3437 5.72866H1.59939C1.10796 5.72866 0.734375 6.126 0.734375 6.59368V14.0062C0.734375 14.4976 1.13171 14.8712 1.59939 14.8712H3.11935V17.2096C3.11935 17.9111 3.96155 18.2619 4.45205 17.7704L7.32851 14.894H14.2972C14.7886 14.894 15.1622 14.4966 15.1622 14.0289V6.59354C15.2088 6.12585 14.8351 5.72866 14.3437 5.72866ZM11.7953 12.1831H4.14895C3.89135 12.1831 3.70503 11.973 3.70503 11.7392C3.70503 11.4816 3.91512 11.2953 4.14895 11.2953H11.7953C12.0529 11.2953 12.2392 11.5053 12.2392 11.7392C12.2392 11.9959 12.0519 12.1831 11.7953 12.1831ZM11.7953 9.32956H4.14895C3.89135 9.32956 3.70503 9.11946 3.70503 8.88564C3.70503 8.6518 3.91512 8.44171 4.14895 8.44171H11.7953C12.0529 8.44171 12.2392 8.6518 12.2392 8.88564C12.2392 9.11946 12.0519 9.32956 11.7953 9.32956Z" fill="#0B0C0C"/>
    </svg>
  )
}

const SharedServicesIcon = () => {
  return (
    <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_2315_15019)">
        <path d="M0.0858548 8.28624C-0.105822 9.07861 0.0262478 9.89796 0.454021 10.5942C1.34579 12.0407 3.25786 12.4966 4.71763 11.6175C4.98995 11.4522 5.2354 11.2483 5.43877 11.0115L5.56149 10.8709L10.8068 13.8083L10.7472 13.9888C10.649 14.2947 10.5976 14.6124 10.5976 14.9336C10.5976 16.6262 11.9884 18 13.7006 18C15.4116 18 16.8 16.6262 16.8 14.9336C16.8 13.2409 15.4115 11.8671 13.7006 11.8671C12.803 11.8671 11.9498 12.2539 11.3607 12.9232L11.238 13.0639L5.99273 10.1264L6.05234 9.94359C6.2557 9.32235 6.2557 8.6718 6.05234 8.05641L5.99273 7.87355L11.238 4.93611L11.3607 5.07677C11.9498 5.74608 12.803 6.13288 13.7006 6.13288C15.4116 6.13288 16.8 4.7556 16.8 3.06644C16.8 1.37727 15.4115 0 13.7006 0C11.9896 0 10.5976 1.37376 10.5976 3.06644C10.5976 3.38761 10.649 3.70526 10.7472 4.01119L10.8068 4.19404L5.56149 7.12911L5.43877 6.98845C4.443 5.85733 2.77869 5.60179 1.48363 6.3848C0.774196 6.81381 0.275129 7.48897 0.0834608 8.29073L0.0858548 8.28624Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_2315_15019">
          <rect width="16.8" height="18" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  )
}
